/**
 * The Agent's configuration, exactly as the browser build was given it.
 *
 * `app/vite.config.ts` freezes three things into the bundle: `NODE_ENV`,
 * `VITE_AGENT_APP_URL` (from `ONDIT_APP_ORIGIN`, the same origin the API
 * redirects to) and every other `VITE_AGENT_*` variable in the environment.
 * All of it ships to every visitor, so none of it can be a secret; the
 * server's configuration belongs to the Rust API and is not read here.
 *
 * `env` still answers the server-era names shared modules read
 * (`env.REDIS_URL`, `env.TRELLO_API_KEY`, …). In a browser those were always
 * `undefined` and still are. `getEnv` takes only the names below, so a new
 * read of a variable the build never sets is a compile error rather than a
 * feature that is silently off.
 */

/** Every variable this bundle can be configured by. Unset reads as off. */
export type PublicEnvKey =
  | 'NODE_ENV'
  | 'VITE_AGENT_APP_URL'
  | 'VITE_AGENT_SOCKET_URL'
  | 'VITE_AGENT_E2B_ENABLED'
  | 'VITE_AGENT_ENABLE_PLAYGROUND'
  | 'VITE_AGENT_DISABLE_INVITATIONS'
  | 'VITE_AGENT_DISABLE_PUBLIC_API'

export const env: Readonly<Record<string, string | undefined>> = import.meta.env.AGENT_CONFIG

export function getEnv(key: PublicEnvKey): string | undefined {
  return env[key]
}

export function isTruthy(value: unknown): boolean {
  return typeof value === 'string'
    ? value.toLowerCase() === 'true' || value === '1'
    : value === true
}

// Explicitly false, as opposed to unset — the inverse of `isTruthy`, not its negation.
export function isFalsy(value: unknown): boolean {
  return typeof value === 'string'
    ? value.toLowerCase() === 'false' || value === '0'
    : value === false
}

export function envNumber(
  value: number | string | undefined | null,
  fallback: number,
  options: { min?: number; integer?: boolean } = {}
): number {
  if (value == null || String(value).trim() === '') return fallback
  const number = Number(value)
  return Number.isFinite(number) &&
    number >= (options.min ?? 0) &&
    (!options.integer || Number.isInteger(number))
    ? number
    : fallback
}

// `undefined` when unset so callers can tell "off" from "not configured"; `Boolean('false')` is
// true, which is why this exists rather than a cast.
export function envBoolean(value: boolean | string | undefined | null): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (value === undefined || value === null || value === '') return undefined
  const normalized = String(value).trim().toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on'
}
